import { supportsPassiveEvents } from 'detect-it';
import { getBrowserDimensions, throttle } from 'utils/UiUtils';
import React, { useEffect, useState } from 'react';
import { renderOptions } from '../../utils/renderer';
import CampaignsListItem from '../CampaignsListItem';
import { getCampaignKey } from 'utils/adaya/CampaignUtils';

const ROW_BUFFER = 5;

const renderProbabilityStatement = (probs) => {
    if (probs[0] === probs[1]) {
        return 'increase';
    } else if (probs[0] > 0.5) {
        return 'increase';
    } else if (probs[0] > probs[1] && probs[1] !== 0) {
        return 'increase';
    } else {
        return 'decrease';
    }
};

const renderAction = (code) => {
    let message = "we're monitoring";
    if (code > 0) {
        message = 'scale up';
    }
    if (code < 0) {
        message = 'optimize';
    }

    return {
        message,
        code
    };
};

const renderNoCampaignsMessage = (code) => {
    switch (code) {
        case 1:
            return 'There are currently no campaigns to scale up.';
        case -1:
            return 'There are currently no campaigns to optimize.';
        default:
            return 'There are currently no campaigns to monitor.';
    }
};

function CampaignsList(props) {
    const [ready, setReady] = useState(false);
    const [renderedData, setRenderedData] = useState([]);
    const [scrollPos, setScrollPos] = useState(0);
    const [rowDimensions, setRowDimensions] = useState({ rowHeight: 1, totalHeight: 1 });
    const [indexStart, setIndexStart] = useState(0);
    const [indexEnd, setIndexEnd] = useState(10);

    useEffect(() => {
        setRenderedData(props.data);
    }, [props.data]);

    // allows fade-in after mount
    useEffect(() => {
        // todo: handle this for mobile...
        // possibly use: https://github.com/rafgraph/detect-it
        // addEventListener(document, "touchstart", function(e) {
        //     console.log(e.defaultPrevented);  // will be false
        //     e.preventDefault();   // does nothing since the listener is passive
        //     console.log(e.defaultPrevented);  // still false
        // }, Modernizr.passiveeventlisteners ? {passive: true} : false);

        document.getElementById('campaigns-container').addEventListener('scroll', handleScroll, { passive: supportsPassiveEvents });
        window.addEventListener('resize', handleResize);
        setReady(true);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const listItemIsInViewRange = (datapoint, index) => {
        if (!datapoint.active) {
            datapoint.active = index >= indexStart && index <= indexEnd;
        }
        return datapoint.active;
    };

    useEffect(() => {
        // Resize rows when data is populated
        setTimeout(handleResize, 20);
    }, [renderedData]);

    const handleResize = throttle((e) => {
        const rows = document.querySelectorAll('.campaigns-list .campaign-row');
        if (rows.length > 0) {
            let r = rows[0];
            rowHeight = r.clientHeight;
            totalHeight = rows.length * rowHeight;
            if (rowHeight !== rowDimensions.rowHeight || totalHeight !== rowDimensions.totalHeight) {
                setRowDimensions({ rowHeight, totalHeight });
            }
        }
    });

    // finds scroll position and calculates which rows should render, depending on user's browser resolution.
    const handleScroll = throttle((e) => {
        let _scrollPos = document.getElementById('campaigns-container').scrollTop;
        if (_scrollPos !== scrollPos) {
            setScrollPos(_scrollPos);
        }
    });

    useEffect(() => {
        const { y } = getBrowserDimensions();
        const { rowHeight, totalHeight } = rowDimensions;
        let _indexStart = Math.floor(Math.max(0, (scrollPos - rowHeight * ROW_BUFFER) / rowHeight));
        let _indexEnd = Math.ceil(Math.min(totalHeight, (scrollPos + y + rowHeight * ROW_BUFFER) / rowHeight));
        setIndexStart(_indexStart);
        setIndexEnd(_indexEnd);
    }, [scrollPos, rowDimensions]);

    return (
        <div className={'campaigns-list ' + (ready ? 'ready' : '')}>
            {props.data.length > 0 ? (
                <>
                    {renderedData.map((d, i) => {
                        // const data = getDataMetric(props.selectedMetric, d);
                        const data = d.data;
                        const probability = d.probability;

                        let mx = Math.max(...data);
                        let mn = Math.min(...data);
                        const max = mx > 0 ? mx : 1;
                        const min = mx > 0 ? mn : -0.25;

                        const statement = renderProbabilityStatement(probability);
                        const action = renderAction(d.roasRecommendationCode);
                        const color = statement === 'increase' ? 'green' : 'purple';

                        return (
                            <CampaignsListItem
                                key={getCampaignKey(d.campaign)}
                                mobile={props.mobile}
                                campaign={d.campaign}
                                selectedMetric={props.selectedMetric}
                                summaryValue={d.summaryValue}
                                statement={statement}
                                probability={probability}
                                action={action}
                                data={data}
                                options={renderOptions(max, min, props.selectedMetric)}
                                color={color}
                                index={i}
                                active={listItemIsInViewRange(d, i)}
                                showGraph={true}
                            />
                        );
                    })}
                </>
            ) : (
                <div className="no-campaigns-message">{renderNoCampaignsMessage(props.filteredRecCode)}</div>
            )}
        </div>
    );
}

export default CampaignsList;
